import { readItem, removeItem, writeItem } from '../../core';
import { DIAGNOSIS_FLOW } from '../onboarding/flow';
import {
  SURVEY_STEPS,
  isSurveyStepAnswered,
  pickAnswers,
  surveyStepById,
} from '../onboarding/survey';

import type { OnboardingAnswer } from '../onboarding/flow';
import type { SurveyStepId } from '../onboarding/survey';

/**
 * profile — 초기 설정이 남기는 **프로필**의 정본(client-only, 서버 없음).
 *
 * 프로필은 회차와 무관하게 유지되는 답이다. 어느 답이 그런지는 목록을 따로 적지 않고
 * **플로우에서 유도한다**: 설문 단계 중 진단 플로우가 다시 묻지 않는 것. 측정환경·컨디션은
 * 회차마다 달라 진단이 다시 묻고(DIAGNOSIS_FLOW), 그래서 프로필이 아니다. 목록을 손으로
 * 들면 설문 단계를 하나 더할 때 완료 판정·저장·프로필 화면 셋이 조용히 어긋난다.
 *
 * 드래프트와 키를 나누는 이유: 드래프트는 플로우가 끝나면 지워지는 임시물이고 프로필은
 * 영속이다. 한 키에 두면 회차를 끝낼 때 프로필이 같이 지워지거나, 프로필을 고쳤다는
 * 이유로 진행 중인 회차가 다른 자리에서 열린다.
 *
 * 네이티브 셸은 `localStorage.setItem`을 가로채 AsyncStorage에 미러하고 콜드스타트에
 * 되돌려 준다(`apps/mobile/src/utils/injectedScripts.ts`) — 키 이름을 가리지 않는 인터셉트라
 * 이 키도 그 경로를 탄다.
 */

export const PROFILE_KEY = 'muzly.profile.v2';

/** 프로필을 이루는 설문 단계 — 설문 순서 그대로, 진단이 다시 묻는 것은 뺀다. */
export const PROFILE_KEYS: readonly SurveyStepId[] = SURVEY_STEPS.map(
  (step) => step.id,
).filter((id) => !DIAGNOSIS_FLOW.steps.includes(id));

export type ProfileKey = SurveyStepId;

export interface Profile {
  version: 2;
  answers: Record<string, OnboardingAnswer>;
  /** 초기 설정을 마친 시각(epoch ms). */
  completedAt: number;
}

const PROFILE_VERSION = 2;

/** 프로필 답이 전부 설문 검증을 통과하나 — 공백 닉네임은 답이 아니다(survey.ts와 같은 잣대). */
export const isProfileComplete = (
  answers: Record<string, OnboardingAnswer>,
): boolean =>
  PROFILE_KEYS.every((key) => {
    const config = surveyStepById(key);
    return config !== undefined && isSurveyStepAnswered(config, answers);
  });

/** 모르는 스키마 버전은 버린다 — 마이그레이션 대신 폐기(diagnosisStore와 같은 정책). */
export const loadProfile = (): Profile | null => {
  const raw = readItem(PROFILE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const profile = JSON.parse(raw) as Partial<Profile>;
    return profile.version === PROFILE_VERSION &&
      typeof profile.answers === 'object' &&
      profile.answers !== null
      ? {
          version: PROFILE_VERSION,
          answers: profile.answers,
          completedAt: profile.completedAt ?? 0,
        }
      : null;
  } catch {
    return null;
  }
};

/** 프로필 키만 골라 저장하고 저장한 것을 돌려준다 — 화면이 저장소를 다시 읽지 않게. */
export const saveProfile = (
  answers: Record<string, OnboardingAnswer>,
  completedAt: number = Date.now(),
): Profile => {
  const profile: Profile = {
    version: PROFILE_VERSION,
    answers: pickAnswers(answers, PROFILE_KEYS),
    completedAt,
  };
  writeItem(PROFILE_KEY, JSON.stringify(profile));
  return profile;
};

/**
 * 답 하나를 고치고 **고친 뒤 프로필**을 낸다 — 화면이 저장소를 다시 읽지 않게.
 * 완료 시각은 그대로다: 수정은 완료가 아니다. 프로필이 없으면(게이트 밖에서 열린 화면)
 * 그 답 하나로 만든다 — 완료 시각 0 = 아직 설정을 마치지 않았다는 뜻.
 */
export const updateProfileAnswer = (
  key: ProfileKey,
  value: OnboardingAnswer,
): Profile => {
  const current = loadProfile();
  return saveProfile(
    { ...(current?.answers ?? {}), [key]: value },
    current?.completedAt ?? 0,
  );
};

/**
 * 옛 저장소 키 — 전부 **옛 어휘**로 쓰인 답을 담고 있다(`nickname`·`wannabeStyle`,
 * `secret`·`beginner`·`someNoise` …). 설문 단계 id와 선택지 값이 서버 계약 이름으로
 * 바뀌었으므로 그 답은 지금 설문의 잣대를 통과하지 못한다.
 */
export const LEGACY_STORAGE_KEYS: readonly string[] = [
  // 재작성 전 한 키 — 설문 답·진행 위치·리포트가 같이 있었다.
  'muzly.onboarding.draft.v1',
  'muzly.profile.v1',
  'muzly.setup.draft.v1',
  'muzly.diagnosis.draft.v1',
];

/**
 * 옛 키를 지운다 — 옮기지 않고 버린다.
 *
 * 값을 새 어휘로 변환해 살릴 수도 있지만, 변환표는 한 번 쓰고 영영 남는 코드이고
 * 살아나는 사람은 프로덕션 OAuth가 꺼져 있던 기간의 사용자뿐이다. 버리면 그 사람은
 * 설문을 한 번 다시 본다 — `RequireProfile`이 프로필 없음을 보고 `/setup`을 연다.
 *
 * 프로필을 읽는 자리(게이트)가 읽기 전에 한 번 부른다. 두 번 돌아도 같은 결과고,
 * 옛 키가 없으면 아무것도 안 한다.
 */
export const purgeLegacyStorage = (): void => {
  for (const key of LEGACY_STORAGE_KEYS) {
    removeItem(key);
  }
};
